// src/screens/DiscountsScreen.js
// ─────────────────────────────────────────────────────────────
// Admin management for the Discount primitive (backend model:
// backend/src/models/Discount.js). One screen handles all three scopes
// (guest / host / global) and all three value types (percent / fixed $ /
// prepaid balance). The main table lets an operator filter + search and
// pick any row to see the Redemption history in a side drawer; a top
// "New discount" button opens a create modal with progressive disclosure
// — the visible fields change based on the chosen scope and type so the
// operator isn't overwhelmed with irrelevant inputs.

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  FiPlus, FiSearch, FiRefreshCw, FiX, FiUser, FiHome, FiGlobe,
  FiPercent, FiDollarSign, FiCreditCard, FiSlash, FiTrash2,
} from 'react-icons/fi'
import { DiscountService } from '../services/DiscountService'
import HostSearchPicker from '../components/pickers/HostSearchPicker'

const SCOPE_OPTIONS = [
  { value: 'global', label: 'Global', Icon: FiGlobe, help: 'Anyone who knows the code' },
  { value: 'guest',  label: 'Guest',  Icon: FiUser,  help: 'Reserved for a specific guest email' },
  { value: 'host',   label: 'Host',   Icon: FiHome,  help: 'Valid on any order for that host\u2019s properties' },
]

const TYPE_OPTIONS = [
  { value: 'percent', label: 'Percent off',      Icon: FiPercent,    help: '% off the order total' },
  { value: 'fixed',   label: 'Fixed $ off',      Icon: FiDollarSign, help: 'Flat dollar amount off each redemption' },
  { value: 'balance', label: 'Prepaid balance',  Icon: FiCreditCard, help: 'Draws down a $ bucket across many orders. Great for hosts buying credit.' },
]

const fmtMoney = (n) =>
  Number.isFinite(Number(n))
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n))
    : '—'

const fmtDate = (d) => {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString() } catch { return '—' }
}

function ScopePill({ scope }) {
  const opt = SCOPE_OPTIONS.find(s => s.value === scope) || SCOPE_OPTIONS[0]
  const Icon = opt.Icon
  const tone = scope === 'host'  ? 'bg-indigo-100 text-indigo-800'
            : scope === 'guest' ? 'bg-emerald-100 text-emerald-800'
            :                     'bg-slate-100 text-slate-700'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <Icon className="h-3 w-3" /> {opt.label}
    </span>
  )
}

function TypePill({ type }) {
  const opt = TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0]
  const Icon = opt.Icon
  const tone = type === 'balance' ? 'bg-amber-100 text-amber-800'
            : type === 'fixed'   ? 'bg-rose-100 text-rose-800'
            :                      'bg-sky-100 text-sky-800'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <Icon className="h-3 w-3" /> {opt.label}
    </span>
  )
}

function describeValue(d) {
  if (d.type === 'percent') return `${d.percentOff}% off`
  if (d.type === 'fixed')   return `${fmtMoney(d.fixedOff)} off`
  if (d.type === 'balance') return `${fmtMoney(d.balanceRemaining)} of ${fmtMoney(d.balanceTotal)}`
  return '—'
}

export default function DiscountsScreen() {
  const [discounts, setDiscounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [scopeFilter, setScopeFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('true')
  const [search, setSearch] = useState('')

  const [showCreate, setShowCreate] = useState(false)
  const [detail, setDetail] = useState(null) // { discount, redemptions, loading }

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = {}
      if (scopeFilter)  params.scope = scopeFilter
      if (activeFilter) params.active = activeFilter
      if (search)       params.q = search
      const list = await DiscountService.list(params)
      setDiscounts(list)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load discounts')
    } finally {
      setLoading(false)
    }
  }, [scopeFilter, activeFilter, search])

  useEffect(() => { load() }, [load])

  const openDetail = async (d) => {
    setDetail({ discount: d, redemptions: [], loading: true })
    try {
      const list = await DiscountService.redemptions(d._id)
      setDetail({ discount: d, redemptions: list, loading: false })
    } catch {
      setDetail({ discount: d, redemptions: [], loading: false })
    }
  }

  const deactivate = async (d) => {
    if (!window.confirm(`Deactivate "${d.code || d.label || 'this discount'}"? Redemption history is preserved.`)) return
    try {
      await DiscountService.deactivate(d._id)
      load()
      if (detail?.discount?._id === d._id) setDetail(null)
    } catch (err) {
      alert(err?.response?.data?.error || 'Deactivate failed')
    }
  }

  const totals = useMemo(() => {
    const sum = {
      count: discounts.length,
      balanceOutstanding: 0,
      balanceSold: 0,
    }
    for (const d of discounts) {
      if (d.type === 'balance') {
        sum.balanceOutstanding += Number(d.balanceRemaining || 0)
        sum.balanceSold        += Number(d.balanceTotal || 0)
      }
    }
    return sum
  }, [discounts])

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Discounts</h1>
            <p className="text-sm text-gray-500">
              Guest-scoped promos, host credit balances, and global codes in one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FiRefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              <FiPlus className="h-4 w-4" /> New discount
            </button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Active discounts" value={totals.count} tone="slate" />
          <Stat label="Outstanding host credit" value={fmtMoney(totals.balanceOutstanding)} tone="amber" />
          <Stat label="Total credit sold" value={fmtMoney(totals.balanceSold)} tone="emerald" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-6 py-3">
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, label, or guest email"
            className="rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">All scopes</option>
          {SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Active + inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        {error ? (
          <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">{error}</span>
        ) : null}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              <tr>
                <th className="px-4 py-3">Code / Label</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : discounts.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">No discounts yet. Click "New discount" to create one.</td></tr>
              ) : discounts.map((d) => (
                <tr
                  key={d._id}
                  onClick={() => openDetail(d)}
                  className={`cursor-pointer ${d.active ? 'hover:bg-indigo-50/40' : 'bg-gray-50/60 text-gray-500 hover:bg-gray-100'}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-sm font-semibold text-gray-900">{d.code || <span className="italic text-gray-400">(auto-apply)</span>}</div>
                    {d.label ? <div className="text-xs text-gray-500">{d.label}</div> : null}
                  </td>
                  <td className="px-4 py-3"><ScopePill scope={d.scope} /></td>
                  <td className="px-4 py-3"><TypePill type={d.type} /></td>
                  <td className="px-4 py-3">{describeValue(d)}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {d.scope === 'guest' ? d.guestEmail || '—'
                      : d.scope === 'host' ? (d.hostId ? `Host ${String(d.hostId).slice(-6)}` : '—')
                      : 'Anyone'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {d.uses || 0}{d.maxUses ? ` / ${d.maxUses}` : ''}
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtDate(d.expiresAt)}</td>
                  <td className="px-4 py-3">
                    {d.active ? (
                      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Active</span>
                    ) : (
                      <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {d.active ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deactivate(d) }}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                      >
                        <FiSlash className="h-3 w-3" /> Deactivate
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate ? (
        <CreateDiscountModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load() }}
        />
      ) : null}

      {detail ? (
        <DiscountDetailDrawer
          detail={detail}
          onClose={() => setDetail(null)}
          onChanged={load}
          onDeactivate={() => deactivate(detail.discount)}
        />
      ) : null}
    </div>
  )
}

function Stat({ label, value, tone = 'slate' }) {
  const toneCls = {
    slate:   'bg-slate-50   text-slate-900 border-slate-200',
    amber:   'bg-amber-50   text-amber-900 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  }[tone] || 'bg-slate-50 text-slate-900 border-slate-200'
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-xl font-bold">{value}</div>
    </div>
  )
}

function CreateDiscountModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    scope: 'global',
    type: 'percent',
    code: '',
    label: '',
    guestEmail: '',
    hostId: '',
    percentOff: '',
    fixedOff: '',
    balanceTotal: '',
    autoApply: false,
    maxUses: '',
    perGuestLimit: '',
    minOrderAmount: '',
    maxDiscountPerOrder: '',
    expiresAt: '',
    notes: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState('')

  const set = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value
    setForm((f) => ({ ...f, [k]: v }))
  }

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSubmitting(true)
    try {
      const payload = { ...form }
      // Drop empty strings so the backend defaults kick in.
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k] })
      await DiscountService.create(payload)
      onCreated()
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message || 'Create failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-bold text-gray-900">New discount</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Scope picker */}
        <Label>Scope</Label>
        <div className="mb-4 grid grid-cols-3 gap-2">
          {SCOPE_OPTIONS.map((o) => {
            const active = form.scope === o.value
            const Icon = o.Icon
            return (
              <button
                key={o.value} type="button"
                onClick={() => setForm((f) => ({ ...f, scope: o.value, autoApply: o.value === 'host' ? f.autoApply : false }))}
                className={`flex flex-col items-start rounded-xl border p-3 text-left text-sm transition ${active ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <Icon className={`mb-1 h-4 w-4 ${active ? 'text-indigo-600' : 'text-gray-500'}`} />
                <span className="font-semibold text-gray-900">{o.label}</span>
                <span className="mt-0.5 text-xs text-gray-500">{o.help}</span>
              </button>
            )
          })}
        </div>

        {/* Type picker */}
        <Label>Value type</Label>
        <div className="mb-4 grid grid-cols-3 gap-2">
          {TYPE_OPTIONS.map((o) => {
            const active = form.type === o.value
            const Icon = o.Icon
            return (
              <button
                key={o.value} type="button"
                onClick={() => setForm((f) => ({ ...f, type: o.value }))}
                className={`flex flex-col items-start rounded-xl border p-3 text-left text-sm transition ${active ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <Icon className={`mb-1 h-4 w-4 ${active ? 'text-indigo-600' : 'text-gray-500'}`} />
                <span className="font-semibold text-gray-900">{o.label}</span>
                <span className="mt-0.5 text-xs text-gray-500">{o.help}</span>
              </button>
            )
          })}
        </div>

        {/* Scope targets */}
        {form.scope === 'guest' ? (
          <Row>
            <Field label="Guest email *">
              <input type="email" required value={form.guestEmail} onChange={set('guestEmail')} className={inputCls} />
            </Field>
          </Row>
        ) : null}
        {form.scope === 'host' ? (
          <Row>
            <Field label="Host *" hint="Search by name or email — the _id is captured automatically.">
              <HostSearchPicker
                value={form.hostId}
                onChange={(hostId) =>
                  // Keep the form-state setter shape consistent with the
                  // other `set('field')` helpers by synthesizing an event.
                  set('hostId')({ target: { value: hostId } })
                }
                autoFocus
              />
            </Field>
            <Field label="Auto-apply at checkout?" hint="Recommended for prepaid balance credit">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={form.autoApply} onChange={set('autoApply')} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                <span className="text-sm text-gray-700">Auto-apply (no code entry needed)</span>
              </label>
            </Field>
          </Row>
        ) : null}

        {/* Code + label */}
        <Row>
          <Field label={form.type === 'balance' && form.autoApply ? 'Code (optional for auto-apply)' : 'Code'} hint="Case-insensitive. Leave blank for auto-apply balance credits.">
            <input value={form.code} onChange={set('code')} className={inputCls} />
          </Field>
          <Field label="Label (admin only)">
            <input value={form.label} onChange={set('label')} className={inputCls} />
          </Field>
        </Row>

        {/* Value fields by type */}
        {form.type === 'percent' ? (
          <Row>
            <Field label="Percent off *">
              <input type="number" min="1" max="100" step="1" required value={form.percentOff} onChange={set('percentOff')} className={inputCls} />
            </Field>
          </Row>
        ) : null}
        {form.type === 'fixed' ? (
          <Row>
            <Field label="Dollar amount off per redemption *">
              <input type="number" min="0.01" step="0.01" required value={form.fixedOff} onChange={set('fixedOff')} className={inputCls} />
            </Field>
          </Row>
        ) : null}
        {form.type === 'balance' ? (
          <Row>
            <Field label="Prepaid balance total ($) *" hint="This is what the host pre-pays. It draws down on each redemption.">
              <input type="number" min="1" step="0.01" required value={form.balanceTotal} onChange={set('balanceTotal')} className={inputCls} />
            </Field>
          </Row>
        ) : null}

        {/* Limits */}
        <Row>
          <Field label="Max uses" hint="0 = unlimited">
            <input type="number" min="0" value={form.maxUses} onChange={set('maxUses')} className={inputCls} />
          </Field>
          <Field label="Per-guest limit" hint="0 = unlimited">
            <input type="number" min="0" value={form.perGuestLimit} onChange={set('perGuestLimit')} className={inputCls} />
          </Field>
        </Row>
        <Row>
          <Field label="Min order $" hint="Only applies if order subtotal ≥ this">
            <input type="number" min="0" step="0.01" value={form.minOrderAmount} onChange={set('minOrderAmount')} className={inputCls} />
          </Field>
          <Field label="Max discount per order ($)" hint="0 = no cap">
            <input type="number" min="0" step="0.01" value={form.maxDiscountPerOrder} onChange={set('maxDiscountPerOrder')} className={inputCls} />
          </Field>
        </Row>
        <Row>
          <Field label="Expires at" hint="Optional. Leave empty for no expiry">
            <input type="date" value={form.expiresAt} onChange={set('expiresAt')} className={inputCls} />
          </Field>
        </Row>
        <Row>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={set('notes')} className={inputCls} />
          </Field>
        </Row>

        {err ? <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div> : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60">
            {submitting ? 'Creating…' : 'Create discount'}
          </button>
        </div>
      </form>
    </div>
  )
}

function DiscountDetailDrawer({ detail, onClose, onChanged, onDeactivate }) {
  const { discount: d, redemptions, loading } = detail
  const [topUp, setTopUp] = useState('')
  const [busy, setBusy] = useState(false)

  const doTopUp = async () => {
    if (!topUp || Number(topUp) <= 0) return
    setBusy(true)
    try {
      await DiscountService.update(d._id, { topUp: Number(topUp) })
      setTopUp('')
      onChanged()
      onClose()
    } catch (err) {
      alert(err?.response?.data?.error || 'Top-up failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Discount</div>
            <div className="mt-0.5 font-mono text-lg font-bold text-gray-900">{d.code || <span className="italic text-gray-400">(auto-apply)</span>}</div>
            <div className="mt-1 flex items-center gap-2">
              <ScopePill scope={d.scope} />
              <TypePill type={d.type} />
              {d.active
                ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Active</span>
                : <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700">Inactive</span>}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 text-sm">
          <KV k="Label" v={d.label || '—'} />
          <KV k="Value" v={describeValue(d)} />
          {d.scope === 'guest'  ? <KV k="Guest email" v={d.guestEmail || '—'} /> : null}
          {d.scope === 'host'   ? <KV k="Host" v={d.hostId ? String(d.hostId) : '—'} /> : null}
          {d.autoApply          ? <KV k="Auto-apply" v="Yes (no code entry needed)" /> : null}
          <KV k="Uses" v={`${d.uses || 0}${d.maxUses ? ` / ${d.maxUses}` : ''}`} />
          {d.perGuestLimit       ? <KV k="Per-guest limit" v={d.perGuestLimit} /> : null}
          {d.minOrderAmount      ? <KV k="Min order" v={fmtMoney(d.minOrderAmount)} /> : null}
          {d.maxDiscountPerOrder ? <KV k="Max per order" v={fmtMoney(d.maxDiscountPerOrder)} /> : null}
          <KV k="Expires" v={fmtDate(d.expiresAt)} />
          <KV k="Created" v={fmtDate(d.createdAt)} />
          {d.createdByName ? <KV k="Created by" v={d.createdByName} /> : null}
          {d.stripeCouponId ? <KV k="Stripe coupon" v={d.stripeCouponId} /> : null}
          {d.notes ? <KV k="Notes" v={d.notes} /> : null}

          {d.type === 'balance' && d.active ? (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Top up balance</div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number" min="0" step="0.01"
                  placeholder="0.00"
                  value={topUp} onChange={(e) => setTopUp(e.target.value)}
                  className="w-32 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                />
                <button
                  type="button" onClick={doTopUp} disabled={busy || !topUp}
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  {busy ? 'Adding…' : 'Add to balance'}
                </button>
              </div>
              <p className="mt-1 text-xs text-amber-700">Adds to both total and remaining balance. Useful when a host buys more credit.</p>
            </div>
          ) : null}

          <div className="mt-6">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Redemption history</div>
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">Loading…</div>
            ) : redemptions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-4 text-center text-sm text-gray-500">No redemptions yet.</div>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {redemptions.map((r) => (
                  <li key={r._id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">
                        Order {r.orderUniqueId || String(r.orderId).slice(-6)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmtDate(r.appliedAt)} · {r.guestEmail || '—'}
                        {r.code ? ` · code ${r.code}` : ' · auto-apply'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{fmtMoney(r.amount)}</div>
                      {r.balanceAfter != null ? (
                        <div className="text-[11px] text-gray-500">Balance after: {fmtMoney(r.balanceAfter)}</div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-5 py-3">
          {d.active ? (
            <button
              type="button"
              onClick={onDeactivate}
              className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            >
              <FiTrash2 className="h-4 w-4" /> Deactivate
            </button>
          ) : <span />}
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Close
          </button>
        </div>
      </aside>
    </div>
  )
}

// ─── tiny presentational helpers ────────────────────────────────────

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20'

function Label({ children }) {
  return <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{children}</div>
}

function Row({ children }) {
  return <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-gray-500">{hint}</p> : null}
    </div>
  )
}

function KV({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{k}</span>
      <span className="max-w-[60%] break-words text-right text-gray-900">{v}</span>
    </div>
  )
}
